import React, {
  useState,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { uuid } from "uuidv4";

import styles from "./style.module.scss";
import {
  CustomerOrderResponse,
  PaymentStatus,
  PaymentType,
  CustomerAddressType,
  CustomerAddress,
} from "../../generated/graphql";
import OrderDetails from "../../components/OrderDetail";
import ContactConfirmationForm from "../../components/ContactConfirmationForm";
import ShippingAddressForm from "../../components/ShippingAddressForm";
import {
  ContactFields,
  PaymentMethodState,
  CustomerPaymentReceipt,
  AddressFieldsState,
} from "../../types";
import PaymentMethodForm from "../../components/PaymentMethodForm";
import { validatePhone } from "../../utils/validators";
import { ToastContext } from "../../contexts/ToastContext";
import { detectAttachmentType } from "../../models/attachment";
import { useCustomerSubmit, useUploadAsset } from "./api";
import {
  MAX_RECEIPT_FILE,
  FILE_UPLOAD_SIZE_LIMIT,
  MAX_RETRY_QUERY_PAYMENT_COUNT,
  PAYMENT_PROCESS_PERIOD,
} from "../../constants";
import { useOrderQueryToken } from "../../useCases/token";
import PaymmentSubmissionSuccess from "../../components/PaymentSubmissionSuccess";
import {
  useCustomerPaymentMethodsQuery,
  useCustomerInitiatePayment,
  useCustomerQueryPayment,
} from "../../useCases/customerPayment";
import {
  getRequestResource,
  isRequestLoading,
  isRequestLoaded,
} from "../../models/RequestState";
import LoadingSpinner from "../../components/LoadingSpinner";
import TopBar from "../../components/TopBar";
import { CompanyInfo } from "../../models/company";
import OrderDetailTitle from "../../components/OrderDetailTitle";
import {
  checkShouldButtonDisabled,
  ViewState,
  checkShouldWalletButtonDisabled,
} from "./logic";
import { wait } from "../../utils/wait";
import {
  useGetReducedCustomer,
  useCustomerRemoveAddress,
} from "../../useCases/customer";

export function isFileTypeSupported(file: File): boolean {
  return file.type.startsWith("image/");
}

interface OrderPaymentScreenProps {
  company: CompanyInfo;
  customerOrder: CustomerOrderResponse;
}

const OrderPaymentScreen: React.FC<OrderPaymentScreenProps> = (
  props: OrderPaymentScreenProps
) => {
  const { company, customerOrder } = props;
  const { presentToast } = useContext(ToastContext);

  const paymentFormRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<
    "pending" | "success"
  >("pending");
  const [contactFields, setContactFields] = useState<ContactFields>({
    name: customerOrder.shippingInfo.name || "",
    phone: customerOrder.shippingInfo.phone || "",
    errors: {
      phone: false,
    },
  });
  const [shippingAddress, setShippingAddress] = useState<AddressFieldsState>({
    type: "new",
    country: customerOrder.shippingInfo.country || "HKG",
    address1: customerOrder.shippingInfo.address1 || "",
    address2: customerOrder.shippingInfo.address2 || "",
    saveForFutureUse: false,
  });
  const [paymentMethodState, setPaymentMethodState] = useState<
    PaymentMethodState
  >({
    type: "none",
  });
  const scrollToPaymentForm = useCallback(() => {
    if (paymentFormRef.current != null) {
      paymentFormRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, []);
  useEffect(() => {
    if (paymentMethodState.type !== "none") {
      scrollToPaymentForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodState.type]);
  const handleRemovePaymentReceiptAttachment = useCallback((id: string) => {
    setPaymentMethodState(prev => {
      if (prev.type !== "custom") return prev;
      return {
        ...prev,
        receiptAttachments: [
          ...prev.receiptAttachments.filter(a => a.id !== id),
        ],
      };
    });
  }, []);

  const {
    requestState: fetchPaymentMethodState,
  } = useCustomerPaymentMethodsQuery();
  const companyPaymentMethodList = useMemo(() => {
    return getRequestResource(fetchPaymentMethodState)?.customerPaymentMethods;
  }, [fetchPaymentMethodState]);

  const { requestState: fetchReducedCustomerState } = useGetReducedCustomer();
  const [shippingAddressList, setShippingAddressList] = useState<
    CustomerAddress[]
  >([]);
  const [billingAddressList, setBillingAddressList] = useState<
    CustomerAddress[]
  >([]);
  useEffect(() => {
    if (isRequestLoaded(fetchReducedCustomerState)) {
      const addresses = getRequestResource(fetchReducedCustomerState)
        .reducedCustomer.addresses;
      const fetchedShippingAddressList = addresses.filter(
        a => a.type === CustomerAddressType.Shipping
      );
      setShippingAddressList(fetchedShippingAddressList);
      if (fetchedShippingAddressList.length > 0) {
        setShippingAddress({
          type: "reuse",
          id: fetchedShippingAddressList[0].id,
          country: fetchedShippingAddressList[0].country || "",
          address1: fetchedShippingAddressList[0].address1 || "",
          address2: fetchedShippingAddressList[0].address2 || "",
          saveForFutureUse: false,
        });
      }
      setBillingAddressList(
        addresses.filter(a => a.type === CustomerAddressType.Billing)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchReducedCustomerState.type]);

  const removeCustomerAddress = useCustomerRemoveAddress();
  const handleRemoveAddress = useCallback(
    (address: CustomerAddress) => {
      if (address.type === CustomerAddressType.Billing) {
        setBillingAddressList(prev => {
          return prev.filter(a => a.id !== address.id);
        });
      }
      if (address.type === CustomerAddressType.Shipping) {
        setShippingAddressList(prev => {
          return prev.filter(a => a.id !== address.id);
        });
      }
      removeCustomerAddress(address.id).then(
        () => {
          console.log("Done");
        },
        e => {
          console.log("Error: ", e);
        }
      );
    },
    [removeCustomerAddress]
  );

  const uploadAssetWithProgress = useUploadAsset();
  const uploadFile = useCallback(
    (file: File) => {
      if (paymentMethodState.type !== "custom") return;
      const fileReader = new FileReader();
      fileReader.onloadend = async () => {
        const type = detectAttachmentType(file.name);
        const newAttachment: CustomerPaymentReceipt = {
          id: uuid(),
          type,
          fileUrl: fileReader.result as string,
          fileName: file.name,
          fileSize: file.size,
          uploadedSize: 0,
          assetId: null,
        };
        setPaymentMethodState(prev => {
          if (prev.type !== "custom") return prev;
          return {
            ...prev,
            receiptAttachments: [newAttachment, ...prev.receiptAttachments],
          };
        });
        const onUploadProgress = (e: ProgressEvent) => {
          setPaymentMethodState(prev => {
            if (prev.type !== "custom") return prev;
            const index = prev.receiptAttachments.findIndex(
              a => a.id === newAttachment.id
            );
            if (index !== -1) {
              const attachment = prev.receiptAttachments[index];
              attachment.uploadedSize = e.loaded;
            }
            return {
              ...prev,
              receiptAttachments: [...prev.receiptAttachments],
            };
          });
        };
        try {
          const assetId = await uploadAssetWithProgress(file, onUploadProgress);
          setPaymentMethodState(prev => {
            if (prev.type !== "custom") return prev;
            const index = prev.receiptAttachments.findIndex(
              a => a.id === newAttachment.id
            );
            if (index !== -1) {
              const attachment = prev.receiptAttachments[index];
              attachment.uploadedSize = attachment.fileSize;
              attachment.assetId = assetId;
            }
            return {
              ...prev,
              receiptAttachments: [...prev.receiptAttachments],
            };
          });
        } catch {
          presentToast(
            {
              content: "view.paymentMethodForm.uploadFileFail",
              values: {
                filename: file.name,
              },
            },
            true
          );
          handleRemovePaymentReceiptAttachment(newAttachment.id);
        }
      };
      fileReader.readAsDataURL(file);
    },
    [
      paymentMethodState,
      uploadAssetWithProgress,
      presentToast,
      handleRemovePaymentReceiptAttachment,
    ]
  );
  const uploadCustomerPaymentReceiptFiles = useCallback(
    (files: File[]) => {
      if (paymentMethodState.type !== "custom") return;
      if (
        paymentMethodState.receiptAttachments.length + files.length >
        MAX_RECEIPT_FILE
      ) {
        presentToast(
          {
            content: "view.paymentMethodForm.sendTooManyFiles",
          },
          true
        );
        return;
      }
      if (files.some(file => file.size > FILE_UPLOAD_SIZE_LIMIT)) {
        presentToast(
          { content: "view.paymentMethodForm.sendTooLargeFiles" },
          true
        );
        return;
      }
      if (files.some(file => !isFileTypeSupported(file))) {
        presentToast(
          { content: "view.paymentMethodForm.sendUnsupportedFiles" },
          true
        );
        return;
      }
      for (const file of files) {
        uploadFile(file);
      }
    },
    [paymentMethodState, presentToast, uploadFile]
  );

  const onSuccess = useCallback(() => setSubmissionStatus("success"), []);
  const customerSubmit = useCustomerSubmit();
  const token = useOrderQueryToken();
  const onSubmit = useCallback(async () => {
    if (token === undefined) return;
    if (paymentMethodState.type !== "custom") return;
    if (
      paymentMethodState.receiptAttachments.length === 0 ||
      paymentMethodState.receiptAttachments[0].assetId === null
    )
      return;
    // Field validation
    setContactFields(prev => ({
      ...prev,
      errors: { ...prev.errors, phone: false },
    }));
    const isPhoneNumberValid = validatePhone(contactFields.phone);
    if (!isPhoneNumberValid) {
      setContactFields(prev => ({
        ...prev,
        errors: {
          ...prev.errors,
          phone: !isPhoneNumberValid,
        },
      }));
      presentToast(
        { content: "view.contactConfirmationForm.incorrectInput" },
        true
      );
      return;
    }
    setIsLoading(true);
    try {
      const result = await customerSubmit({
        paymentMethodId: paymentMethodState.id,
        customer: {
          name: contactFields.name,
          phone: contactFields.phone,
        },
        shipping: {
          country: shippingAddress.country,
          address1: shippingAddress.address1,
          address2: shippingAddress.address2,
        },
        shippingSave: shippingAddress.saveForFutureUse,
        token,
        receipt: paymentMethodState.receiptAttachments[0].assetId || "",
      });
      if (!result) {
        presentToast({ content: "screen.paymentInfoScreen.fail" }, true);
      } else {
        onSuccess();
      }
    } catch {
      presentToast({ content: "screen.paymentInfoScreen.fail" }, true);
    } finally {
      setIsLoading(false);
    }
  }, [
    contactFields,
    shippingAddress,
    paymentMethodState,
    token,
    customerSubmit,
    presentToast,
    onSuccess,
  ]);

  const initiatePayment = useCustomerInitiatePayment();
  const callInitiatePayment = useCallback(
    async (billing?: AddressFieldsState) => {
      const stripePaymentId = companyPaymentMethodList?.find(
        p => p.type === PaymentType.Stripe
      )?.id;
      if (stripePaymentId === undefined) return null;
      setContactFields(prev => ({
        ...prev,
        errors: { ...prev.errors, phone: false },
      }));
      const isPhoneNumberValid = validatePhone(contactFields.phone);
      if (!isPhoneNumberValid) {
        setContactFields(prev => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone: !isPhoneNumberValid,
          },
        }));
        presentToast(
          { content: "view.contactConfirmationForm.incorrectInput" },
          true
        );
        return null;
      }

      const billingAddress = billing
        ? billing
        : paymentMethodState.type === "stripeCreditCard"
        ? paymentMethodState.isUsingShipmentAddressForBilling
          ? shippingAddress
          : paymentMethodState.billingAddress
        : null;

      return initiatePayment({
        paymentMethodId: stripePaymentId,
        customer: {
          name: contactFields.name,
          phone: contactFields.phone,
        },
        shipping: {
          country: shippingAddress.country,
          address1: shippingAddress.address1,
          address2: shippingAddress.address2,
        },
        shippingSave: shippingAddress.saveForFutureUse,
        billing:
          paymentMethodState.type !== "custom"
            ? {
                country: billingAddress?.country || "",
                address1: billingAddress?.address1 || "",
                address2: billingAddress?.address2 || "",
              }
            : null,
        billingSave:
          paymentMethodState.type === "stripeCreditCard"
            ? paymentMethodState.billingAddress.saveForFutureUse
            : false,
      });
    },
    [
      companyPaymentMethodList,
      contactFields,
      shippingAddress,
      initiatePayment,
      presentToast,
      paymentMethodState,
    ]
  );

  const queryPayment = useCustomerQueryPayment();
  const callPaymentQueryPolling = useCallback(
    async (paymentId: string) => {
      // Retry 3 times with 3 second interval to check payment status
      let paymentComplete = false;
      for (
        let retryCount = MAX_RETRY_QUERY_PAYMENT_COUNT;
        retryCount > 0;
        retryCount--
      ) {
        await wait(PAYMENT_PROCESS_PERIOD);
        const payment = await queryPayment(paymentId);
        if (payment.status === PaymentStatus.Captured) {
          paymentComplete = true;
          break;
        }
      }
      return paymentComplete;
    },
    [queryPayment]
  );

  const isButtonDisabled = useMemo(
    () =>
      checkShouldButtonDisabled(
        contactFields,
        customerOrder,
        shippingAddress,
        paymentMethodState
      ),
    [contactFields, customerOrder, shippingAddress, paymentMethodState]
  );

  const isWalletButtonDisabled = useMemo(
    () =>
      checkShouldWalletButtonDisabled(
        contactFields,
        customerOrder,
        shippingAddress
      ),
    [contactFields, customerOrder, shippingAddress]
  );

  const viewState: ViewState = useMemo(() => {
    if (submissionStatus === "pending") {
      return {
        type: "pending",
        isLoading,
        contactFields,
        shippingAddress,
        paymentMethodState,
        isButtonDisabled,
        isWalletButtonDisabled,
        customerOrder,
        paymentMethodList: companyPaymentMethodList || [],
        isPaymentMethodLoading: isRequestLoading(fetchPaymentMethodState),
      };
    }
    return {
      type: "success",
      order: customerOrder.order,
      shippingInfo: customerOrder.shippingInfo,
      paymentType: paymentMethodState.type,
    };
  }, [
    submissionStatus,
    isLoading,
    contactFields,
    shippingAddress,
    fetchPaymentMethodState,
    customerOrder,
    isButtonDisabled,
    paymentMethodState,
    companyPaymentMethodList,
    isWalletButtonDisabled,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.paymentInfo}>
        <TopBar companyInfo={company} />
        {viewState.type === "pending" && (
          <>
            <OrderDetailTitle customerOrder={viewState.customerOrder} />
            <OrderDetails
              customerOrder={viewState.customerOrder}
              showReconfirmNotice={true}
            />
            <ContactConfirmationForm
              contact={viewState.contactFields}
              setContact={setContactFields}
              isDisabled={viewState.isLoading}
            />
            {customerOrder.order.shipmentRequired && (
              <ShippingAddressForm
                addressState={viewState.shippingAddress}
                setAddress={setShippingAddress}
                isDisabled={viewState.isLoading}
                addressList={shippingAddressList}
                handleRemoveAddress={handleRemoveAddress}
              />
            )}
            {viewState.isPaymentMethodLoading ? (
              <div className={styles.loadingContainer}>
                <LoadingSpinner />
              </div>
            ) : (
              <PaymentMethodForm
                ref={paymentFormRef}
                order={viewState.customerOrder.order}
                paymentMethodList={viewState.paymentMethodList}
                paymentMethodState={viewState.paymentMethodState}
                setPaymentMethodState={setPaymentMethodState}
                uploadReceiptFile={uploadCustomerPaymentReceiptFiles}
                handleRemoveAsset={handleRemovePaymentReceiptAttachment}
                isWalletButtonDisabled={viewState.isWalletButtonDisabled}
                isDisabled={viewState.isButtonDisabled}
                isLoading={viewState.isLoading}
                addressList={billingAddressList}
                setIsLoading={setIsLoading}
                initiatePayment={callInitiatePayment}
                paymentQueryPolling={callPaymentQueryPolling}
                onSubmit={onSubmit}
                onSuccess={onSuccess}
                handleRemoveAddress={handleRemoveAddress}
              />
            )}
          </>
        )}
        {viewState.type === "success" && (
          <PaymmentSubmissionSuccess
            order={viewState.order}
            shippingInfo={viewState.shippingInfo}
            paymentType={viewState.paymentType}
          />
        )}
      </div>
    </div>
  );
};

export default OrderPaymentScreen;
